import type { APIClient } from '../../apiClient';
import { workflowRoutes } from '../workflows/workflowRoutes';
import { libraryRoutes } from './libraryRoutes';
import type {
  SnippetMetadata,
  SnippetWithPublishedVersion,
  SnippetVersionDetails,
} from '~/dashboard/views/workflows/service/snippets/types';
import type {
  SnippetsResponse,
  SnippetVersionsListResponse,
  CreatedSnippetWorkflowResponse,
  SnippetFiltersResponse,
  SnippetApiFilters,
} from '~/helpers/api-new/modules/library/types';

export type CreateOrUpdateSnippetMetadata = Partial<Omit<SnippetMetadata, 'id' | 'token' | 'integrations' | 'date_created' | 'date_updated'>>;

export type UpdateSnippetMetadataData = {
  snippet_metadata: CreateOrUpdateSnippetMetadata;
};

type CreateSnippetData = {
  snippet_token: string;
  snippet_metadata: CreateOrUpdateSnippetMetadata;
};

type CreateSnippetWorkflowData = {
  name: string;
  type: string;
  is_snippet: boolean;
  workflow_sku_id: number;
};

export type GetSnippetsParams = {
  pageNum?: number;
  pageSize?: number;
  filters?: SnippetApiFilters;
  sort?: {
    field: string;
    direction: 'ASC' | 'DESC';
  };
};

const libraryApiMetaConfig = {
  silent: true,
  skipTransformResponseBody: true,
};

// Query param field mapping: frontend camelCase -> API snake_case (only for query params!)
const queryFieldNameMapping: Record<string, string> = {
  dateCreated: 'date_created',
  dateUpdated: 'date_updated',
  workflowType: 'workflow_type',
};

// Transform frontend filters to API format
function transformFiltersToApi(filters: SnippetApiFilters): SnippetApiFilters {
  const apiFilters: SnippetApiFilters = {};

  Object.entries(filters).forEach(([frontendField, filterValue]) => {
    const apiField = queryFieldNameMapping[frontendField] || frontendField;
    apiFilters[apiField] = filterValue;
  });

  return apiFilters;
}

// Transform frontend sort field to API field
function transformSortFieldToApi(field: string): string {
  return queryFieldNameMapping[field] || field;
}

export default function createLibraryAPI(apiClient: APIClient) {
  return {
    getSnippets: async (params?: GetSnippetsParams): Promise<SnippetsResponse> => {
      const queryParams = new URLSearchParams();

      if (params?.pageNum) queryParams.append('pageNum', params.pageNum.toString());
      if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params?.filters) {
        const apiFilters = transformFiltersToApi(params.filters);
        queryParams.append('filters', JSON.stringify(apiFilters));
      }
      if (params?.sort) {
        const apiField = transformSortFieldToApi(params.sort.field);
        queryParams.append('sort', JSON.stringify([[apiField, params.sort.direction]]));
      }

      const url = `${libraryRoutes.snippets}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get<SnippetsResponse>(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Get Snippets',
        },
      });

      // Convert date strings to Date objects
      const parsed: SnippetsResponse = {
        ...response,
        snippets: response.snippets.map(s => ({
          ...s,
          id: s.token,
          date_created: new Date(s.date_created),
          date_updated: new Date(s.date_updated),
        })),
      };

      return parsed;
    },
    getSnippet: (token: string): Promise<SnippetWithPublishedVersion> => {
      const url = libraryRoutes.snippet(token);

      return apiClient.get(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Get Snippet',
        },
      });
    },
    getSnippetVersion: (token: string, version: number): Promise<SnippetVersionDetails> => {
      const url = libraryRoutes.snippetVersion(token, version);

      return apiClient.get(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Get Snippet Version',
        },
      });
    },
    listSnippetVersions: (token: string): Promise<SnippetVersionsListResponse> => {
      const url = libraryRoutes.snippetVersions(token);

      return apiClient.get(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'List Snippet Versions',
        },
      });
    },
    updateSnippetMetadata: (token: string, data: UpdateSnippetMetadataData) => {
      const url = libraryRoutes.snippetMetadata(token);

      return apiClient.put(url, data, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Update Snippet Metadata',
        },
      });
    },
    createSnippet: (data: CreateSnippetData) => {
      const url = libraryRoutes.snippets;

      return apiClient.post(url, data, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Create Snippet',
        },
      });
    },
    createSnippetWorkflow: (data: CreateSnippetWorkflowData) => (
      apiClient.post<CreatedSnippetWorkflowResponse>(workflowRoutes.root, data, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Create Snippet Workflow',
        },
      })
    ),
    publishSnippet: (token: string, versionNumber: number) => {
      const url = libraryRoutes.publishSnippet(token);

      return apiClient.patch(url, { version: versionNumber }, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Publish Snippet',
        },
      });
    },
    unpublishSnippet: (token: string) => {
      const url = libraryRoutes.unpublishSnippet(token);

      return apiClient.patch(url, {}, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Unpublish Snippet',
        },
      });
    },
    archiveSnippet: (token: string) => {
      const url = libraryRoutes.archiveSnippet(token);

      return apiClient.patch(url, {}, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Archive Snippet',
        },
      });
    },
    unarchiveSnippet: (token: string) => {
      const url = libraryRoutes.unarchiveSnippet(token);

      return apiClient.patch(url, {}, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Unarchive Snippet',
        },
      });
    },
    getFilters: (): Promise<SnippetFiltersResponse> => {
      const url = libraryRoutes.filters;

      return apiClient.get(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Get Snippet Filters',
        },
      });
    },
  };
}
